import React, { useState, useEffect } from 'react';
import { descargasAPI, talleresAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Download, Search, FileText, Building2, Calendar, 
  TrendingUp, Loader2, RefreshCw, BarChart3, Clock,
  ChevronLeft, ChevronRight
} from 'lucide-react';

// Helper para ordenar talleres por número ANAC
const sortTalleresByAnac = (talleres) => {
  return [...talleres].sort((a, b) => {
    const anacA = a.numero_anac || '';
    const anacB = b.numero_anac || '';
    return anacA.localeCompare(anacB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const AdminDescargas = () => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [descargas, setDescargas] = useState([]);
  const [talleres, setTalleres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  
  // Filtros
  const [filterTaller, setFilterTaller] = useState('');
  const [filterFecha, setFilterFecha] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Paginación
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadDescargas();
  }, [page, filterTaller, filterFecha]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [estRes, talleresRes] = await Promise.all([
        descargasAPI.getEstadisticas(),
        talleresAPI.getAll({ limit: 500 })
      ]);
      setEstadisticas(estRes.data);
      // Manejar respuesta paginada o array directo y ordenar por ANAC
      const talleresData = talleresRes.data?.data || talleresRes.data || [];
      setTalleres(sortTalleresByAnac(Array.isArray(talleresData) ? talleresData : []));
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const loadDescargas = async () => {
    setLoadingList(true);
    try {
      const params = { page, limit: 20 };
      if (filterTaller) params.taller_id = filterTaller;
      if (filterFecha) params.fecha_desde = filterFecha;
      
      const response = await descargasAPI.getListado(params);
      setDescargas(response.data.descargas || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error cargando descargas:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Este incluye hora, así que usamos el Date completo pero con timezone local
    const date = new Date(dateStr);
    // Ajustar por timezone si es necesario
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDescargas = descargas.filter(d => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      d.documento_nombre?.toLowerCase().includes(term) ||
      d.taller_nombre?.toLowerCase().includes(term) ||
      d.usuario_email?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Registro de Descargas</h1>
          <p className="text-slate-500">Seguimiento de todas las descargas de documentos</p>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Descargas</p>
                <p className="text-3xl font-bold text-slate-800">{estadisticas?.total_descargas || 0}</p>
              </div>
              <div className="p-3 bg-teal-100 rounded-xl">
                <Download className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Hoy</p>
                <p className="text-3xl font-bold text-green-600">{estadisticas?.descargas_hoy || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Esta Semana</p>
                <p className="text-3xl font-bold text-blue-600">{estadisticas?.descargas_semana || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Este Mes</p>
                <p className="text-3xl font-bold text-purple-600">{estadisticas?.descargas_mes || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info destacada */}
      {(estadisticas?.documento_mas_descargado || estadisticas?.taller_mas_activo) && (
        <div className="grid md:grid-cols-2 gap-4">
          {estadisticas?.documento_mas_descargado && (
            <Card className="bg-gradient-to-r from-teal-50 to-white border-teal-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-teal-600" />
                  <div>
                    <p className="text-sm text-teal-600 font-medium">Documento más descargado</p>
                    <p className="text-lg font-semibold text-slate-800">{estadisticas.documento_mas_descargado}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {estadisticas?.taller_mas_activo && (
            <Card className="bg-gradient-to-r from-orange-50 to-white border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Building2 className="h-8 w-8 text-orange-600" />
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Taller más activo</p>
                    <p className="text-lg font-semibold text-slate-800">{estadisticas.taller_mas_activo}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historial de Descargas</CardTitle>
          <CardDescription>Total: {total} registros</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar documento, taller o usuario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterTaller} onValueChange={(v) => { setFilterTaller(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar por taller" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los talleres</SelectItem>
                {talleres.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.razon_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filterFecha}
              onChange={(e) => { setFilterFecha(e.target.value); setPage(1); }}
              className="w-[180px]"
              placeholder="Desde fecha"
            />
          </div>

          {/* Tabla de descargas */}
          {loadingList ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            </div>
          ) : filteredDescargas.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Download className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No hay descargas registradas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Fecha</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Documento</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Taller</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Usuario</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDescargas.map((descarga, idx) => (
                    <tr key={descarga.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {formatDate(descarga.fecha_descarga)}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800 truncate max-w-[200px]">
                            {descarga.documento_nombre}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{descarga.taller_nombre}</p>
                          <p className="text-xs text-slate-500">ANAC: {descarga.taller_anac}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {descarga.usuario_email}
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant={
                          descarga.nivel_suscripcion === 'premium' ? 'default' :
                          descarga.nivel_suscripcion === 'estandar' ? 'secondary' : 'outline'
                        }>
                          {descarga.nivel_suscripcion}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Página {page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDescargas;
